import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { machineId, materialId, purchaseUnitOf, smallPerBigOf } from './wms-master.seed'
import { RETURN_STAGING_ZONE_ID } from './wms-return.seed'
import {
  type TobRtnActionName,
  type TobRtnActorCode,
  type TobRtnStatusCode,
  WF_TOBRTN
} from './workflow-tobreturn.seed'
import { type TrfActionName, type TrfActorCode, type TrfStatusCode, WF_TRF } from './workflow-trf.seed'
import { WF_WIP, type WipActionName, type WipActorCode, type WipStatusCode } from './workflow-wip.seed'

/**
 * C07 WIP Transfer — ERD v010: transfer(+_line), wip_return(+_line), rm_tobacco_return(+_line).
 * Angka dummy dari BA 12C §WIP-01 / §TRF-01 (prototype `jal-v7.44`). Kolom lokal di luar ERD (dicatat laporan 11):
 * transfer.cancel_*, transfer_line.qty_alloc, wip_return.spk_no/request_id/note, wip_return_line.unit_id/ng_reason/
 * good_unit_id/ng_unit_id/note, rm_tobacco_return.note + _line.machine_id/unit_id/note.
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()

const audit = (userId: string, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})

type Step<N extends string, A extends string> = [N, A, string, string, string?]

/* ------------------------------------------------------------------ transfer */

type TrfSeed = {
  no: string
  by: string
  at: string
  status: TrfStatusCode
  lines: [string, number][]
  steps: Step<TrfActionName, TrfActorCode>[]
  cancelRequested?: string
  sji?: string
  departed?: string
  staged?: 'STAGING' | 'TRANSIT'
}

const TRANSFERS: TrfSeed[] = [
  {
    no: 'ITR-260814-001',
    by: 'agus',
    at: '2026-08-14 09:10',
    status: 'TRF_REQUESTED',
    lines: [
      ['ETK-BOLD20', 4],
      ['FLT-B06', 40]
    ],
    steps: [['ACT_TRF_SEND', 'ACT_TRF_ADMIN_SGI', 'agus', '2026-08-14 09:12']]
  },
  {
    no: 'ITR-260815-002',
    by: 'agus',
    at: '2026-08-15 10:00',
    status: 'TRF_PENDING_SPV',
    lines: [['PPR-REG', 6]],
    steps: [
      ['ACT_TRF_SEND', 'ACT_TRF_ADMIN_SGI', 'agus', '2026-08-15 10:02'],
      ['ACT_TRF_ADMIN_ACCEPT', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-15 11:30']
    ]
  },
  {
    no: 'ITR-260816-003',
    by: 'agus',
    at: '2026-08-16 08:30',
    status: 'TRF_PENDING_KADIV',
    lines: [['ETK-XTRA20', 2]],
    steps: [
      ['ACT_TRF_SEND', 'ACT_TRF_ADMIN_SGI', 'agus', '2026-08-16 08:32'],
      ['ACT_TRF_ADMIN_ACCEPT', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-16 09:10'],
      ['ACT_TRF_APP_SPV', 'ACT_TRF_SPV_RM', 'galih', '2026-08-16 13:45']
    ]
  },
  {
    no: 'ITR-260817-004',
    by: 'agus',
    at: '2026-08-17 08:00',
    status: 'TRF_APPROVED',
    lines: [['ETK-BOLD20', 1]],
    cancelRequested: 'Kebutuhan Sansugi berubah, stok lokal masih cukup.',
    steps: [
      ['ACT_TRF_SEND', 'ACT_TRF_ADMIN_SGI', 'agus', '2026-08-17 08:02'],
      ['ACT_TRF_ADMIN_ACCEPT', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-17 08:40'],
      ['ACT_TRF_APP_SPV', 'ACT_TRF_SPV_RM', 'galih', '2026-08-17 09:30'],
      ['ACT_TRF_APP_KADIV', 'ACT_TRF_KADIV_RM', 'alex', '2026-08-17 10:15']
    ]
  },
  {
    no: 'ITR-260817-005',
    by: 'agus',
    at: '2026-08-17 08:10',
    status: 'TRF_READY_SHIP',
    lines: [['PPR-REG', 2]],
    staged: 'STAGING',
    steps: [
      ['ACT_TRF_SEND', 'ACT_TRF_ADMIN_SGI', 'agus', '2026-08-17 08:12'],
      ['ACT_TRF_ADMIN_ACCEPT', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-17 08:45'],
      ['ACT_TRF_APP_SPV', 'ACT_TRF_SPV_RM', 'galih', '2026-08-17 09:35'],
      ['ACT_TRF_APP_KADIV', 'ACT_TRF_KADIV_RM', 'alex', '2026-08-17 10:20'],
      ['ACT_TRF_ASSIGN', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-17 10:40'],
      ['ACT_TRF_STAGING', 'ACT_TRF_OP_RM', 'karno', '2026-08-17 13:10']
    ]
  },
  {
    no: 'ITR-260817-006',
    by: 'agus',
    at: '2026-08-17 08:20',
    status: 'TRF_IN_TRANSIT',
    lines: [['INN-BLK96', 2]],
    staged: 'TRANSIT',
    sji: '001/SJI/08/2026',
    departed: '2026-08-17',
    steps: [
      ['ACT_TRF_SEND', 'ACT_TRF_ADMIN_SGI', 'agus', '2026-08-17 08:22'],
      ['ACT_TRF_ADMIN_ACCEPT', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-17 08:50'],
      ['ACT_TRF_APP_SPV', 'ACT_TRF_SPV_RM', 'galih', '2026-08-17 09:40'],
      ['ACT_TRF_APP_KADIV', 'ACT_TRF_KADIV_RM', 'alex', '2026-08-17 10:25'],
      ['ACT_TRF_ASSIGN', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-17 10:45'],
      ['ACT_TRF_STAGING', 'ACT_TRF_OP_RM', 'tono', '2026-08-17 12:00'],
      ['ACT_TRF_DISPATCH', 'ACT_TRF_ADMIN_RM', 'yulia', '2026-08-17 15:30']
    ]
  }
]

/* --------------------------------------------------------------- WIP return */

type WipSeed = {
  no: string
  spk: string
  by: string
  at: string
  status: WipStatusCode
  note: string
  lines: [string, number, string][]
  steps: Step<WipActionName, WipActorCode>[]
}

const WIP_RETURNS: WipSeed[] = [
  {
    no: 'RWIP-260818-001',
    spk: '39-08/JLC/26',
    by: 'joko',
    at: '2026-08-18 09:40',
    status: 'WIP_PENDING_CHECK',
    note: 'Sisa produksi shift pagi',
    lines: [
      ['FLT-B06', 1, 'Dos utuh, segel belum dibuka'],
      ['INN-BLK96', 1, 'Roll sisa, sudah dibungkus ulang'],
      ['OPP-125', 1, '']
    ],
    steps: [['ACT_WIP_SEND', 'ACT_WIP_PRODUKSI', 'joko', '2026-08-18 09:40']]
  }
]

/* ---------------------------------------------------------- tobacco return */

type TobSeed = {
  no: string
  by: string
  at: string
  status: TobRtnStatusCode
  note: string
  lines: [string, string, number][]
  steps: Step<TobRtnActionName, TobRtnActorCode>[]
}

const TOBACCO_RETURNS: TobSeed[] = [
  {
    no: 'TBR-260817-001',
    by: 'joko',
    at: '2026-08-17 15:10',
    status: 'TOB_RTN_CHECKED',
    note: 'Sisa giling Mollins 1',
    lines: [['MOLLINS-1', 'TSG-REG', 15]],
    steps: [
      ['ACT_TOB_RTN_SEND', 'ACT_TOB_RTN_PRODUKSI', 'joko', '2026-08-17 15:10'],
      ['ACT_TOB_RTN_VERIFY', 'ACT_TOB_RTN_ADMIN_RM', 'yulia', '2026-08-17 16:00']
    ]
  },
  {
    no: 'TBR-260818-001',
    by: 'joko',
    at: '2026-08-18 11:20',
    status: 'TOB_RTN_PENDING_CHECK',
    note: '',
    lines: [['MOLLINS-1', 'TSG-REG', 3]],
    steps: [['ACT_TOB_RTN_SEND', 'ACT_TOB_RTN_PRODUKSI', 'joko', '2026-08-18 11:20']]
  }
]

const BIG_UNIT: Record<string, string> = {
  'ETK-BOLD20': 'Box',
  'ETK-XTRA20': 'Box',
  'FLT-B06': 'Dos',
  'PPR-REG': 'Roll',
  'INN-BLK96': 'Roll',
  'OPP-125': 'Roll',
  'TSG-REG': 'Sak'
}

const unitRow = (
  n: number,
  material: string,
  qr: string,
  qtyBig: number,
  patch: Record<string, unknown>,
  by: string,
  at: string
): LocalRow => ({
  ...audit(USER_IDS[by], at),
  id: seedId(SEED_GROUP.rmUnit, 400 + n),
  uid: `UNIT-${String(400 + n).padStart(6, '0')}`,
  qr_code: qr,
  material_id: materialId(material),
  batch_no: `${material}-WIP`,
  supplier_id: null,
  po_no: null,
  qty_small: qtyBig * smallPerBigOf(material),
  rack_bin_id: null,
  warehouse_zone_id: null,
  flag: 'REGULAR',
  unit_status: 'IN_STOCK',
  qc_date: utc(at),
  unit_big_snapshot: BIG_UNIT[material] ?? null,
  small_per_big_snapshot: smallPerBigOf(material),
  is_leftover_wip: false,
  stored_at: utc(at),
  ...patch
})

export function buildWmsTransferSeed(): SeedTables {
  const rm_unit: LocalRow[] = []
  const picking_task: LocalRow[] = []
  const picking_task_item: LocalRow[] = []
  const transfer: LocalRow[] = []
  const transfer_line: LocalRow[] = []
  const wip_return: LocalRow[] = []
  const wip_return_line: LocalRow[] = []
  const rm_tobacco_return: LocalRow[] = []
  const rm_tobacco_return_line: LocalRow[] = []
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  let unitCounter = 0
  let logCounter = 0

  const submit = <S extends string, A extends string, N extends string>(
    wf: {
      workflowId: string
      statusId: (code: S) => string
      actorId: (code: A) => string
      actionId: (name: N) => string
      actionStatus: Record<N, [S, S]>
    },
    entity: string,
    entityId: string,
    submissionId: string,
    status: S,
    at: string,
    by: string,
    steps: Step<N, A>[]
  ) => {
    wxl_submission.push({
      ...audit(USER_IDS[by], at),
      id: submissionId,
      workflow_id: wf.workflowId,
      entity,
      entity_id: entityId,
      source: 'ACTION',
      current_status_id: wf.statusId(status),
      last_action_id: wf.actionId(steps[steps.length - 1][0]),
      workflow_snapshot: null
    })
    steps.forEach(([action, actor, who, when, reason]) => {
      const [from, to] = wf.actionStatus[action]
      logCounter += 1
      wxl_submission_log.push({
        ...audit(USER_IDS[who], when),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 6000 + logCounter),
        workflow_id: wf.workflowId,
        submission_id: submissionId,
        action_id: wf.actionId(action),
        actor_id: wf.actorId(actor),
        from_status_id: wf.statusId(from),
        to_status_id: wf.statusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason: reason ?? null,
        entity,
        entity_id: entityId
      })
    })
  }

  TRANSFERS.forEach((trf, index) => {
    const id = seedId(SEED_GROUP.transfer, index + 1)
    transfer.push({
      ...audit(USER_IDS[trf.by], trf.at),
      id,
      itr_no: trf.no,
      direction: 'UTAMA→SGI',
      status_id: WF_TRF.statusId(trf.status),
      submission_id: seedId(SEED_GROUP.wxlSubmission, 600 + index + 1),
      sji_no: trf.sji ?? null,
      departed_at: trf.departed ?? null,
      shortage_pr_id: null,
      cancel_requested: Boolean(trf.cancelRequested),
      cancel_reason: trf.cancelRequested ?? null,
      cancel_requested_at: trf.cancelRequested ? utc('2026-08-17 11:00') : null,
      is_in_transit: trf.status === 'TRF_IN_TRANSIT'
    })
    const lineIds = trf.lines.map(([material, qty], lineIndex) => {
      const lineId = seedId(SEED_GROUP.transferLine, (index + 1) * 10 + lineIndex)
      transfer_line.push({
        ...audit(USER_IDS[trf.by], trf.at),
        id: lineId,
        transfer_id: id,
        material_id: materialId(material),
        unit_id: purchaseUnitOf(material),
        qty_requested: qty,
        qty_alloc: ['TRF_REQUESTED', 'TRF_PENDING_SPV', 'TRF_REJECTED'].includes(trf.status) ? null : qty,
        qty_received: 0
      })
      return lineId
    })
    if (trf.staged) {
      const taskId = seedId(SEED_GROUP.pickingTask, 700 + index + 1)
      const operator = trf.staged === 'STAGING' ? 'karno' : 'tono'
      picking_task.push({
        ...audit(USER_IDS.yulia, '2026-08-17 10:40'),
        id: taskId,
        pick_no: `PICK-26081${trf.staged === 'STAGING' ? 7 : 7}-9${index}`,
        type: 'TRANSFER',
        doc_ref: trf.no,
        operator_id: USER_IDS[operator],
        task_status: 'COMPLETED',
        machine_id: null,
        area_code: 'ZS-RM'
      })
      trf.lines.forEach(([material, qty], lineIndex) => {
        unitCounter += 1
        const unit = unitRow(
          unitCounter,
          material,
          `${material}:20260801:01:09${unitCounter}`,
          qty,
          {
            unit_status: trf.staged === 'STAGING' ? 'IN_TRANSFER_STAGING' : 'IN_TRANSIT',
            warehouse_zone_id: trf.staged === 'STAGING' ? RETURN_STAGING_ZONE_ID : null
          },
          'yulia',
          '2026-08-01 08:00'
        )
        rm_unit.push(unit)
        picking_task_item.push({
          ...audit(USER_IDS[operator], '2026-08-17 12:00'),
          id: seedId(SEED_GROUP.pickingTaskItem, 700 + unitCounter),
          task_id: taskId,
          unit_id: unit.id,
          line_id: null,
          transfer_line_id: lineIds[lineIndex],
          unit_name: unit.unit_big_snapshot,
          qty_plan: qty,
          qty_taken: qty,
          from_rack_bin_id: null,
          from_warehouse_zone_id: null,
          is_wip: false,
          picked_at: utc('2026-08-17 12:00'),
          handover_id: null,
          status: 'PICKED'
        })
      })
    }
    submit(
      WF_TRF,
      'transfer',
      id,
      seedId(SEED_GROUP.wxlSubmission, 600 + index + 1),
      trf.status,
      trf.at,
      trf.by,
      trf.steps
    )
  })

  WIP_RETURNS.forEach((wip, index) => {
    const id = seedId(SEED_GROUP.wipReturn, index + 1)
    wip_return.push({
      ...audit(USER_IDS[wip.by], wip.at),
      id,
      rwip_no: wip.no,
      spk_ref: wip.spk,
      spk_no: wip.spk,
      putaway_operator_id: null,
      status_id: WF_WIP.statusId(wip.status),
      submission_id: seedId(SEED_GROUP.wxlSubmission, 700 + index + 1),
      note: wip.note
    })
    wip.lines.forEach(([material, qty, note], lineIndex) => {
      wip_return_line.push({
        ...audit(USER_IDS[wip.by], wip.at),
        id: seedId(SEED_GROUP.wipReturnLine, (index + 1) * 10 + lineIndex),
        wip_return_id: id,
        material_id: materialId(material),
        unit_id: purchaseUnitOf(material),
        qty_reported: qty,
        qty_good: null,
        qty_ng: null,
        ng_reason_id: null,
        ng_reason: null,
        note: note || null,
        good_unit_id: null,
        ng_unit_id: null
      })
    })
    submit(
      WF_WIP,
      'wip_return',
      id,
      seedId(SEED_GROUP.wxlSubmission, 700 + index + 1),
      wip.status,
      wip.at,
      wip.by,
      wip.steps
    )
  })

  TOBACCO_RETURNS.forEach((tob, index) => {
    const id = seedId(SEED_GROUP.tobaccoReturn, index + 1)
    rm_tobacco_return.push({
      ...audit(USER_IDS[tob.by], tob.at),
      id,
      return_no: tob.no,
      machine_id: machineId(tob.lines[0][0] as 'MOLLINS-1'),
      status_id: WF_TOBRTN.statusId(tob.status),
      submission_id: seedId(SEED_GROUP.wxlSubmission, 800 + index + 1),
      note: tob.note || null
    })
    tob.lines.forEach(([machine, material, qty], lineIndex) => {
      let unitRef: string | null = null
      if (tob.status === 'TOB_RTN_CHECKED') {
        unitCounter += 1
        const unit = unitRow(
          unitCounter,
          material,
          `${material}:20260817:W1:000${lineIndex + 1}`,
          qty,
          {
            unit_status: 'AWAITING_PUTAWAY',
            is_leftover_wip: true,
            stored_at: null,
            batch_no: `${material}-WIP-260817`
          },
          'yulia',
          '2026-08-17 16:00'
        )
        rm_unit.push(unit)
        unitRef = unit.id as string
      }
      rm_tobacco_return_line.push({
        ...audit(USER_IDS[tob.by], tob.at),
        id: seedId(SEED_GROUP.tobaccoReturnLine, (index + 1) * 10 + lineIndex),
        return_id: id,
        machine_id: machineId(machine as 'MOLLINS-1'),
        material_id: materialId(material),
        qty_sak: qty,
        unit_id: unitRef,
        note: null
      })
    })
    submit(
      WF_TOBRTN,
      'rm_tobacco_return',
      id,
      seedId(SEED_GROUP.wxlSubmission, 800 + index + 1),
      tob.status,
      tob.at,
      tob.by,
      tob.steps
    )
  })

  return {
    rm_unit,
    picking_task,
    picking_task_item,
    transfer,
    transfer_line,
    wip_return,
    wip_return_line,
    rm_tobacco_return,
    rm_tobacco_return_line,
    wxl_submission,
    wxl_submission_log
  }
}
